import React from "react";
import { Spinner } from "react-bootstrap";
import { Flag } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  EmptyStateWidget,
  getStatusBadgeClassName,
  STATUS_BADGE_COLORS,
  badgeBaseClass,
  isLate,
  isLateStatus
} from "../utils/HomeTabUtils";
import TaskList from "../../../../interfaces/task/TaskList";
import AvatarImage from "../../../ui/Avatar/AvatarImage";
import './HomeTaskTab.css';

interface HomeTaskTabProps {
  tasks: TaskList[];
  loading: boolean;
  currentOrganization: any;
  limit?: number;
}

const HomeTaskTab: React.FC<HomeTaskTabProps> = ({
  tasks,
  loading,
  currentOrganization,
  limit = 4
}) => {
  const navigate = useNavigate();

  if (tasks.length === 0) {
    return (
      <div className="no-data-message">
        {loading ? <span><Spinner size="sm" /> Loading...</span> : <EmptyStateWidget />}
      </div>
    );
  }

  return (
    <div className="common_content_inner mby_t_24">
      <div className="task-list-container">
        {tasks.slice(0, limit).map((item) => (
          <div
            key={item.id}
            className={`task-card ${isLateStatus(item.endDate)}`}
            onClick={() => navigate(`/task/${currentOrganization?.abbreviation}-${item.friendlyId}`)}
          >
            <div className="task-card-content">
              <div className="task-info-left flex items-center min-w-0 flex-1">
                <div className="task-assignees flex-shrink-0 mr-3">
                  <AvatarImage className="min-w-[20px] min-h-[20px]" userId={""} size={"xs"} />
                </div>

                {/* The container that controls the space */}
                <div className="task-left1 flex-1 min-w-0 flex flex-col justify-center">

                  {/* Header Row: Force it to stay within task-left1 bounds */}
                  <div className="flex items-center gap-2 min-w-0 w-full">
                    <span className="task-id top-header flex-shrink-0">
                      {currentOrganization?.abbreviation}-{item.id}
                    </span>
                    {/* TRUNCATE GOES HERE: This span will now see the "wall" and show ... */}
                    <span className="task-participant top-header truncate min-w-0">
                      {item.participant?.firstName + " " + item.participant?.lastName}
                    </span>
                  </div>

                  {/* Title Row: Force it to stay within task-left1 bounds */}
                  <div className="task-card-body min-w-0 w-full">
                    <h3 className="task-title truncate m-0 leading-tight">
                      {item.title}
                    </h3>
                  </div>

                </div>
              </div>

              <div className="task-info-right flex-shrink-0">
                <div className="flex items-center gap-[1rem] flex-wrap justify-end">
                  <span className={`task-due-date latedue `}>
                    {isLate(item.endDate) ? "Late: " : "Due: "}
                    {(() => {
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      const dueDate = new Date(item.endDate);
                      dueDate.setHours(0, 0, 0, 0);
                      const diffTime = dueDate.getTime() - today.getTime();
                      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                      if (diffDays < 0) return `${Math.abs(diffDays)} days`;
                      else if (diffDays === 0) return "Today";
                      else return `${diffDays} days`;
                    })()}
                  </span>
                  <div className="flex items-center gap-1">
                    <div className="task-assignees">
                      {item.assignees?.filter((a) => !a.isReporter).slice(0, 1).map((a, index) => (
                        <div className="flex items-center gap-1" key={index}>
                          <AvatarImage className="max-w-[1.3rem] max-h-[1.3rem]" userId={a.user.id} size={"xxs"} />
                          <span className="task-assignees flex items-center gap-1">
                            {a.user.firstName?.[0]}. {a?.user?.lastName}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-[1rem] flex-wrap justify-end">
                  <span className={`${badgeBaseClass} ${item.subType?.type === "MONTHLY" ? "bg-[#EA9CEB] !important" :
                    item.subType?.type === "QUARTERLY" ? "bg-[#C1F1F1]" :
                      item.subType?.type === "INTERIM" ? "bg-[#9CC1EB] !important" : "bg-[#F6DDD8]"
                    } ps-task-frequency-new badge-text-due`}>
                    {item.subType?.type || "Monthly"}
                  </span>
                  <span
                    className={`${getStatusBadgeClassName(item.status.name)} ps-task-status-new gap-1`}
                    style={{ "--flag-fill-color": STATUS_BADGE_COLORS[item.status?.name] || "#9ca3af" } as React.CSSProperties}
                  >
                    <Flag size={12} className="hometabs-status-flag" />{" "}
                    {item.status.name === "READY_" ? "READY" : item.status.name}
                  </span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default HomeTaskTab;